import { useMemo } from 'react';
import { BarChart3 } from '../../../../../icons';
import { useMenuGate } from '../../../../../lib/menu-gate';
import { leaveAvailable } from '../../../../../lib/leave-balance';
// 「완료 결재」는 **완료함**으로 간다 — 옛 판과 같은 주소를 쓴다(2026-09-16 · 아래 주석 참조).
import { boxPath } from '../../../../ApprovalPage/box/approval-box-types';
import { WidgetCard } from '../../WidgetCard';
import { useWidgetActions } from '../../widget-context';
import { useWidgetItems } from '../../use-widget-items';
import {
  useCompletedApprovals, useDailyWorkMonth, useLeaveSummary, useMonthEvents, useProjectCount,
} from '../../widget-queries';
import {
  eventMinutes, inRange, periodRange, previousRange, signed, touchesMonth, uniqueById, type Range,
} from './period';
import './widgets-v2.css';

/**
 * 한눈에 v2 — 보이는 지표 · 순서 · 기간 · 지난 기간 비교 (2026-09-16 · 대시보드 시안 v2 V3a · 깃발 켤 때만).
 *
 * ★★★ **옛 위젯의 규칙을 그대로 가져온다** — 모르는 값은 「—」 · 완료 결재가 100건에서 잘리면 「N+」 ·
 *     평균 근무는 기록이 있는 날로만. 달라지는 것은 «무엇을 · 어떤 순서로 · 어느 기간»뿐이다.
 * ★★ 비교 배지는 **두 기간 값을 다 알 때만** — 한쪽을 모르면 배지를 안 그린다(0 과 비교하지 않는다).
 * ★ 주 · 비교가 옆 달에 걸치면 그 달 일정 · 근무만 더 받는다(안 걸치면 부르지 않는다).
 * ★ 「일정 시간」은 종일이 아닌 모든 일정이다(옛 위젯과 같음). 「회의」만 세는 것은 이번 주 요약(Q-10)의 몫이다.
 */

interface Kpi { key: string; label: string; value: string; unit?: string; to?: string; cmp?: string | null }

function hours(min: number | null | undefined): string | null {
  if (min == null || !Number.isFinite(min)) return null;
  return (min / 60).toFixed(1);
}

export function KpiStripWidgetV2() {
  const { go } = useWidgetActions();
  const items = useWidgetItems('kpi-strip');
  const period = items.option<string>('period') === 'week' ? 'week' : 'month';
  const compare = items.option<boolean>('compare') === true;

  const attGate = useMenuGate('attendance', '근태');
  const apprGate = useMenuGate('approval', '전자결재');
  const taskGate = useMenuGate('task', '할일');
  const calGate = useMenuGate('calendar', '캘린더');

  const now = new Date();
  const cur = periodRange(period, now);
  const prev = previousRange(period, cur);
  const ranges: Range[] = compare ? [cur, prev] : [cur];
  const y = now.getFullYear();
  const m = now.getMonth();
  const pm = new Date(y, m - 1, 1);
  const nm = new Date(y, m + 1, 1);
  const needPrev = ranges.some((r) => touchesMonth(r, pm.getFullYear(), pm.getMonth()));
  const needNext = ranges.some((r) => touchesMonth(r, nm.getFullYear(), nm.getMonth()));

  const leave = useLeaveSummary();
  const done = useCompletedApprovals(apprGate.enabled);
  const projects = useProjectCount(taskGate.enabled);
  const evCur = useMonthEvents(y, m, calGate.enabled);
  const evPrev = useMonthEvents(pm.getFullYear(), pm.getMonth(), calGate.enabled && needPrev);
  const evNext = useMonthEvents(nm.getFullYear(), nm.getMonth(), calGate.enabled && needNext);
  const dwCur = useDailyWorkMonth(y, m + 1, attGate.enabled);
  const dwPrev = useDailyWorkMonth(pm.getFullYear(), pm.getMonth() + 1, attGate.enabled && needPrev);

  const kpis = useMemo<Kpi[]>(() => {
    const periodLabel = period === 'week' ? '이번 주' : '이번 달';

    const approvalsIn = (r: Range): number | null => (done.data
      ? done.data.items.filter((d) => inRange(d.completedAt ?? d.draftedAt ?? d.createdAt, r)).length
      : null);

    const eventsKnown = Boolean(evCur.data) && (!needPrev || Boolean(evPrev.data)) && (!needNext || Boolean(evNext.data));
    const events = uniqueById([...(evCur.data ?? []), ...(evPrev.data ?? []), ...(evNext.data ?? [])]);
    const meetingIn = (r: Range): number | null => (eventsKnown
      ? events.filter((e) => inRange(e.startAt, r)).reduce((s, e) => s + eventMinutes(e), 0)
      : null);

    const days = [...(dwCur.data?.days ?? []), ...(dwPrev.data?.days ?? [])];
    const workKnown = dwCur.data != null && (!needPrev || dwPrev.data != null);
    const avgWorkIn = (r: Range): number | null => {
      if (!workKnown) return null;
      const rows = days.filter((d) => d.creditedMinutes > 0 && inRange(`${d.date}T00:00:00`, r));
      return rows.length > 0 ? rows.reduce((s, d) => s + d.creditedMinutes, 0) / rows.length : null;
    };

    const cmp = (a: number | null, b: number | null, digits = 0, unit = '') =>
      (compare && a != null && b != null ? signed(a - b, digits, unit) : null);

    const build: Record<string, () => Kpi> = {
      approval: () => {
        const a = approvalsIn(cur);
        return {
          key: 'approval', label: `${periodLabel} 완료 결재`,
          value: a == null ? '—' : `${a}${done.data?.truncated ? '+' : ''}`,
          /*
           * ★★★ **완료함**으로 간다 (2026-09-16 고침). 옛 판은 이미 그렇게 고쳐져 있었는데
           *     (`FullsizeWidgets.tsx` 의 `boxPath('done')` · 2026-09-15) **V2 로 옮기며 빠졌다** —
           *     「이번 달 **완료** 결재」를 눌렀는데 결재 **대기함**이 열렸다.
           * ⚠ 깃발이 기본 켜짐이 되면 이것이 사용자가 처음 만나는 동작이라, 옮기며 빠진 것을
           *   다시 놓치지 않도록 **V2 와 옛 판 양쪽을 시험으로 잠갔다**(`widget-links.test.tsx`).
           */
          unit: '건', to: boxPath('done'),
          // 잘린 목록이면 지난 기간 수가 틀릴 수 있다 — 그때는 비교하지 않는다.
          cmp: done.data?.truncated ? null : cmp(a, approvalsIn(prev)),
        };
      },
      work: () => {
        const a = avgWorkIn(cur);
        return {
          key: 'work', label: '하루 평균 근무', value: hours(a) ?? '—', unit: '시간', to: '/erp/attendance',
          cmp: cmp(a == null ? null : a / 60, avgWorkIn(prev) == null ? null : avgWorkIn(prev)! / 60, 1, 'h'),
        };
      },
      meeting: () => {
        const a = meetingIn(cur);
        return {
          key: 'meeting', label: `${periodLabel} 일정 시간`, value: hours(a) ?? '—', unit: '시간', to: '/calendar',
          cmp: cmp(a == null ? null : a / 60, meetingIn(prev) == null ? null : meetingIn(prev)! / 60, 1, 'h'),
        };
      },
      leave: () => ({
        key: 'leave', label: '남은 연차',
        value: leaveAvailable(leave.data?.annual)?.toString() ?? '—', unit: '일', to: '/erp/attendance/me',
      }),
      project: () => ({
        key: 'project', label: '진행 프로젝트',
        value: projects.data !== undefined ? String(projects.data) : '—', unit: '개', to: '/project',
      }),
    };

    return items.visible('items').map((k) => build[k]?.()).filter((x): x is Kpi => Boolean(x));
    // items 는 매 렌더 새 객체라 설정이 바뀌면 다시 계산된다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done.data, dwCur.data, dwPrev.data, evCur.data, evPrev.data, evNext.data, leave.data, projects.data,
    period, compare, needPrev, needNext, items]);

  return (
    <WidgetCard
      title="한눈에"
      icon={BarChart3}
      loading={leave.loading && dwCur.loading}
      empty={kpis.length === 0 ? '보여 줄 지표가 없습니다 — 메뉴가 꺼졌거나 모두 껐습니다' : null}
      flush
    >
      <div className="fw-kpis">
        {kpis.map((k) => (
          <button type="button" key={k.key} className="fw-kpi" onClick={() => k.to && go(k.to)} disabled={!k.to}>
            <span className="fw-kpi__k">{k.label}</span>
            <span className="fw-kpi__v">
              {k.value}
              {k.unit && <span className="fw-kpi__u">{k.unit}</span>}
            </span>
            {k.cmp && <span className="fw-kpi__cmp">{period === 'week' ? '지난주' : '지난달'}보다 {k.cmp}</span>}
          </button>
        ))}
      </div>
    </WidgetCard>
  );
}
